// src/pages/ClientPayments.js
// Componente para gestionar todos los pagos del cliente
// Incluye historial de pagos y registro de nuevos pagos
import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    Card,
    CardContent,
    Button,
    TextField,
    MenuItem,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    IconButton,
    Tabs,
    Tab,
    useTheme,
    useMediaQuery,
    FormControlLabel,
    Checkbox,
    ToggleButton,
    ToggleButtonGroup,
    FormControl,
    InputLabel,
    Select,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import {
    Payment as PaymentIcon,
    Receipt as ReceiptIcon,
    AttachFile as AttachFileIcon,
    History as HistoryIcon,
    Add as AddIcon,
    FileUpload as UploadIcon,
    MonetizationOn as MoneyIcon,
    Check as CheckIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    Visibility as ViewIcon,
    Warning as WarningIcon,
    ArrowDownward as DownloadIcon,
    ContentCopy as ContentCopyIcon,
    ChevronRight as ChevronRightIcon,
    Bolt as BoltIcon,
    AccountBalance as BankIcon,
    CreditCard as CardIcon,
    Lock as LockIcon,
    Security as SecurityIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useClientAuth } from '../contexts/ClientAuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import FiberLoader from '../components/common/FiberLoader';

const parseLocalDate = (dateVal) => {
  if (!dateVal) return null;
  if (dateVal instanceof Date) return dateVal;
  const dateStr = String(dateVal);
  const cleanStr = dateStr.split('T')[0].split(' ')[0];
  const parts = cleanStr.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateVal);
};


// Componente TabPanel para las pestañas
const TabPanel = (props) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`payments-tabpanel-${index}`}
            aria-labelledby={`payments-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

const getInvoiceMonthName = (dateString) => {
    if (!dateString) return 'Mensualidad';
    try {
        const date = new Date(dateString);
        const months = [
            'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
        ];
        return `Mes de ${months[date.getMonth()]} ${date.getFullYear()}`;
    } catch (err) {
        return 'Mensualidad';
    }
};

const ClientPayments = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { client, refreshClientStatus } = useClientAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);
    const [payments, setPayments] = useState([]);
    const [pendingInvoices, setPendingInvoices] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [checkedInvoiceIds, setCheckedInvoiceIds] = useState([]);

    const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
    const [openPaymentSelectionDialog, setOpenPaymentSelectionDialog] = useState(false);
    const [paymentDetails, setPaymentDetails] = useState({
        invoice_id: '',
        amount: 0,
        payment_method: '',
        payment_reference: '',
        proof_image_url: ''
    });
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [openPaymentDetailDialog, setOpenPaymentDetailDialog] = useState(false);
    const [requestMerge, setRequestMerge] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [fileUpload, setFileUpload] = useState(null);
    const fileInputRef = useRef(null);
    const hasAutoOpenedRef = useRef(false);

    // Detectar si estamos bajo un WebView restringido (Portal Cautivo de Android)
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const isAndroid = /Android/i.test(ua);
    const isWebView = /wv/i.test(ua) || /Version\/[0-9.]+/i.test(ua);
    const isCaptivePortalApp = /CaptivePortalLogin/i.test(ua);
    const isWebViewRestricted = isCaptivePortalApp || (isAndroid && isWebView);

    // OCR Scanning and Autofill states
    const [ocrAnalyzing, setOcrAnalyzing] = useState(false);
    const [ocrStatus, setOcrStatus] = useState(null); // 'success', 'failed', null
    const [ocrMessage, setOcrMessage] = useState('');
    const [ocrFilledFields, setOcrFilledFields] = useState({
        amount: false,
        reference: false,
        method: false
    });

    const [openAutoApprovedAdvanceModal, setOpenAutoApprovedAdvanceModal] = useState(false);
    const [autoApprovedAdvanceData, setAutoApprovedAdvanceData] = useState({ amount: 0, newBalance: 0 });
    const [pdfModal, setPdfModal] = useState({ open: false, invoiceId: null, format: 'ticket' });

    // Openpay Modal & Error States
    const [openpayOptionsModal, setOpenpayOptionsModal] = useState({
        open: false,
        method: null
    });
    const [openpayStoreModal, setOpenpayStoreModal] = useState({
        open: false,
        reference: '',
        barcodeUrl: '',
        pdfUrl: '',
        transactionId: '',
        amount: 0,
        dueDate: '',
        copied: false,
        checking: false
    });
    const [openpayErrorModal, setOpenpayErrorModal] = useState({
        open: false,
        title: '',
        message: '',
        suggestion: ''
    });
    const [successPaymentModal, setSuccessPaymentModal] = useState({
        open: false,
        title: '¡Pago Procesado Exitosamente!',
        message: 'Tu recibo ha sido cancelado y tu servicio de internet se encuentra activo.',
        gatewayName: 'Pasarela Online',
        transactionId: ''
    });

    const showSuccessPaymentModal = ({
        title = '¡Pago Procesado Exitosamente!',
        message = 'Tu recibo ha sido cancelado y tu servicio de internet se encuentra activo.',
        gatewayName = 'Pasarela Online',
        transactionId = ''
    } = {}) => {
        setSuccessPaymentModal({
            open: true,
            title,
            message,
            gatewayName,
            transactionId
        });
    };

    const showFriendlyPaymentError = (rawMessage, rawCode = '') => {
        const text = String(rawMessage || '').toLowerCase();
        const code = String(rawCode || '').toLowerCase();

        let title = '❌ Transacción No Aprobada';
        let message = rawMessage || 'No se pudo completar el cobro con la tarjeta seleccionada.';
        let suggestion = 'Puedes intentar nuevamente con otra tarjeta o pagar desde tu app de Yape o banca móvil.';

        if (code === '1014' || code === '1005' || text.includes('inactive') || text.includes('inactiva')) {
            title = '⚠️ Cuenta Openpay Inactiva (Sandbox)';
            message = 'La cuenta de comercio en Openpay Sandbox se encuentra inactiva.';
            suggestion = 'Verifica tus credenciales en el Portal Admin o inicia sesión en sandbox-dashboard.openpay.pe para reactivar el Merchant ID.';
        } else if (code === '3003' || text.includes('insufficient') || text.includes('saldo') || text.includes('fondos')) {
            title = '💳 Fondos Insuficientes';
            message = 'La tarjeta no tiene saldo disponible.';
            suggestion = 'Verifica el saldo disponible de tu cuenta o intenta pagar por Yape / BBVA con Código CIP.';
        } else if (code === '3002' || code === '2005' || text.includes('expired') || text.includes('vencida') || text.includes('expirada')) {
            title = '📅 Tarjeta Expirada';
            message = 'La tarjeta está vencida.';
            suggestion = 'Revisa el mes y año de vencimiento ingresados o intenta con una tarjeta vigente.';
        } else if (code === '3001' || code === '3004' || code === '3005' || text.includes('declined') || text.includes('declinada') || text.includes('rechaz')) {
            title = '🚫 Rechazo del Banco Emisor';
            message = 'Rechazo por el banco emisor.';
            suggestion = 'Consulta con tu banco emisor para autorizar la compra o intenta con otra tarjeta.';
        } else if (code === '1001' || text.includes('cvv') || text.includes('security code')) {
            title = '⚠️ Código de Seguridad o Datos Incorrectos';
            message = 'El número de tarjeta o código CVV es incorrecto.';
            suggestion = 'Por favor verifica detenidamente los datos de tu tarjeta e intenta de nuevo.';
        }

        setOpenpayErrorModal({
            open: true,
            title,
            message,
            suggestion
        });
    };

    const handleOpenPdfModal = (invoiceId, defaultFormat = 'ticket') => {
        if (!invoiceId) return;
        setPdfModal({ open: true, invoiceId, format: defaultFormat });
    };

    const handleDownloadInvoice = (invoiceId) => {
        const token = localStorage.getItem('clientToken');
        const downloadUrl = `/api/client/invoices/${invoiceId}/download`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        if (token) {
            link.href = `${downloadUrl}?token=${token}`;
        }
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleCheckboxChange = (invoiceId, isChecked) => {
        const idx = pendingInvoices.findIndex(i => i.id === invoiceId);
        if (idx === -1) return;

        if (isChecked) {
            // Si se marca, también se marcan todas las anteriores (las más antiguas)
            const idsToCheck = pendingInvoices.slice(0, idx + 1).filter(i => !i.has_pending_payment).map(i => i.id);
            setCheckedInvoiceIds(prev => {
                const combined = new Set([...prev, ...idsToCheck]);
                return Array.from(combined);
            });
        } else {
            // Si se desmarca, se desmarcan la actual y todas las posteriores (más nuevas)
            setCheckedInvoiceIds(prev => prev.filter(id => {
                const itemIdx = pendingInvoices.findIndex(i => i.id === id);
                return itemIdx < idx;
            }));
        }
    };

    const handleOpenGroupPaymentDialog = () => {
        const selectedInvoices = pendingInvoices.filter(i => checkedInvoiceIds.includes(i.id));
        if (selectedInvoices.length === 0) return;

        const totalGroupAmount = selectedInvoices.reduce((sum, inv) => {
            const totalAmount = parseFloat(inv.amount);
            const paidAmount = parseFloat(inv.paid_amount || 0);
            return sum + (totalAmount - paidAmount);
        }, 0);

        setSelectedInvoice(selectedInvoices);

        setPaymentDetails({
            invoice_id: selectedInvoices.map(i => i.id).join(','),
            amount: totalGroupAmount,
            payment_method: '',
            payment_reference: '',
            proof_image_url: ''
        });

        setFileUpload(null);
        setRequestMerge(false);
        
        const hasOnlineMethod = paymentMethods.some(method => method.is_online && method.visible_to_client);
        if (hasOnlineMethod) {
            setOpenPaymentSelectionDialog(true);
        } else {
            setOpenPaymentDialog(true);
        }
    };

    const loadCulqiScript = () => {
        return new Promise((resolve) => {
            if (window.CulqiCheckout || window.Culqi) {
                resolve(true);
                return;
            }
            const script = document.createElement('script');
            script.src = 'https://js.culqi.com/checkout-js';
            script.async = true;
            script.onload = () => {
                setTimeout(() => resolve(!!(window.CulqiCheckout || window.Culqi)), 100);
            };
            script.onerror = () => resolve(false);
            document.head.appendChild(script);
        });
    };

    const loadSingleScript = (src, fallbackSrc) => {
        return new Promise((resolve) => {
            const s = document.createElement('script');
            s.src = src;
            s.async = false;
            s.onload = () => resolve(true);
            s.onerror = () => {
                if (fallbackSrc) {
                    const fb = document.createElement('script');
                    fb.src = fallbackSrc;
                    fb.async = false;
                    fb.onload = () => resolve(true);
                    fb.onerror = () => resolve(false);
                    document.head.appendChild(fb);
                } else {
                    resolve(false);
                }
            };
            document.head.appendChild(s);
        });
    };

    const handleStartOpenpayRedirect = async () => {
        try {
            setOpenpayOptionsModal({ open: false, method: null });
            setLoading(true);
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/payments/online/preference', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    invoice_id: paymentDetails.invoice_id,
                    gateway_type: 'openpay'
                })
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || 'Error al iniciar checkout de Openpay');
            }

            const data = await response.json();
            if (data.checkoutUrl) {
                if (data.transactionId) {
                    sessionStorage.setItem('openpay_pending_tx', data.transactionId);
                }
                window.location.href = data.checkoutUrl;
            } else {
                throw new Error('No se recibió la URL de checkout de Openpay.');
            }
        } catch (err) {
            console.error('[Openpay Redirect Error]:', err);
            setLoading(false);
            showFriendlyPaymentError(err.message || 'Error al iniciar la pasarela de Openpay');
        }
    };

    const handleGenerateOpenpayStoreCode = async () => {
        try {
            setOpenpayOptionsModal({ open: false, method: null });
            setLoading(true);
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/payments/online/openpay/charge', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    invoice_id: paymentDetails.invoice_id,
                    method: 'store'
                })
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Error al generar el código de pago en Openpay');
            }

            setOpenpayStoreModal({
                open: true,
                reference: data.reference || '',
                barcodeUrl: data.barcodeUrl || '',
                pdfUrl: data.pdfUrl || '',
                transactionId: data.transactionId || '',
                amount: data.amount || paymentDetails.amount || 0,
                dueDate: data.dueDate || '',
                copied: false,
                checking: false
            });
        } catch (err) {
            console.error('[Openpay Store Code Error]:', err);
            showFriendlyPaymentError(err.message || 'No se pudo generar el código de pago de Openpay.');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectOnlinePayment = async (method) => {
        try {
            setOpenPaymentSelectionDialog(false);

            // Si el cliente selecciona Openpay, mostrar el selector de métodos de Openpay (Tarjeta / Pasarela Oficial vs. Pago Digital / Agentes con Código)
            if (method.gateway_type === 'openpay') {
                setOpenpayOptionsModal({ open: true, method });
                return;
            }

            setLoading(true);
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/payments/online/preference', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    invoice_id: paymentDetails.invoice_id,
                    gateway_type: method.gateway_type
                })
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || 'Error al iniciar pago online');
            }

            const data = await response.json();
            console.log('[Culqi Preference Data Received]:', data);

            if (method.gateway_type === 'culqi') {
                await loadCulqiScript();

                const handleCulqiResponse = async (culqiInstance) => {
                    const tokenObj = culqiInstance.token;
                    const orderObj = culqiInstance.order;

                    if (tokenObj) {
                        console.log('[Culqi Token Creado]:', tokenObj.id);
                        if (typeof culqiInstance.close === 'function') {
                            culqiInstance.close();
                        } else if (window.CulqiCheckout && typeof window.CulqiCheckout.close === 'function') {
                            window.CulqiCheckout.close();
                        }

                        try {
                            const chargeRes = await fetch('/api/client/payments/online/culqi/charge', {
                                method: 'POST',
                                headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                    token: tokenObj.id,
                                    order_id: data.orderId || '',
                                    invoice_id: data.invoiceId
                                })
                            });

                            const chargeData = await chargeRes.json();
                            if (chargeRes.ok && chargeData.success) {
                                showSuccessPaymentModal({
                                    title: '¡Pago Exitoso con Culqi!',
                                    message: 'Su pago con Culqi fue procesado exitosamente. El recibo ha sido cancelado y su servicio está activo.',
                                    gatewayName: 'Culqi',
                                    transactionId: chargeData.transactionId || ''
                                });
                                fetchPaymentsData();
                            } else {
                                showFriendlyPaymentError(chargeData.message, chargeData.errorCode);
                            }
                        } catch (cErr) {
                            showFriendlyPaymentError('Error al conectar con el servidor para procesar el pago.');
                        } finally {
                            setLoading(false);
                        }
                    } else if (orderObj) {
                        console.log('[Culqi Orden / CIP Creado Exitosamente]:', orderObj);
                        setLoading(false);
                        // Permitir que el usuario escanee el código QR o vea su CIP sin cerrar la ventana de Culqi
                    } else if (culqiInstance.error) {
                        console.error('[Culqi Error]:', culqiInstance.error);
                        showFriendlyPaymentError(culqiInstance.error.user_message || culqiInstance.error.merchant_message || 'Error al validar datos con Culqi.');
                        setLoading(false);
                    }
                };

                if (window.Culqi || window.CulqiCheckout) {
                    const settingsConfig = {
                        title: data.title || 'YinoWISP Internet',
                        currency: 'PEN',
                        amount: data.amount,
                        order: data.orderId || ''
                    };

                    const paymentMethodsConfig = {
                        tarjeta: true,
                        yape: true,
                        billetera: true,
                        bancaMovil: true,
                        agente: true,
                        cuotealo: true
                    };

                    const optionsConfig = {
                        lang: 'auto',
                        modal: true,
                        installments: false,
                        paymentMethods: paymentMethodsConfig,
                        paymentMethodsSort: Object.keys(paymentMethodsConfig)
                    };

                    if (window.Culqi) {
                        window.Culqi.publicKey = data.publicKey;
                        window.Culqi.settings(settingsConfig);
                        window.Culqi.options(optionsConfig);
                    }

                    let culqiInstance = window.Culqi;

                    if (window.CulqiCheckout) {
                        try {
                            culqiInstance = new window.CulqiCheckout(data.publicKey, {
                                settings: settingsConfig,
                                client: { email: data.clientEmail || '' },
                                options: optionsConfig
                            });
                        } catch (instErr) {
                            console.warn('[CulqiCheckout Instantiation Warning]:', instErr);
                        }
                    }

                    const activeInstance = culqiInstance || window.Culqi;
                    if (activeInstance) {
                        activeInstance.culqi = () => handleCulqiResponse(activeInstance);
                    }
                    window.culqi = () => handleCulqiResponse(activeInstance || window.Culqi);

                    setLoading(false);
                    if (activeInstance && typeof activeInstance.open === 'function') {
                        activeInstance.open();
                    } else if (window.Culqi && typeof window.Culqi.open === 'function') {
                        window.Culqi.open();
                    }
                } else {
                    setLoading(false);
                    showFriendlyPaymentError('No se pudo cargar la pasarela Culqi.');
                }
            } else if (method.gateway_type === 'openpay') {
                if (data.checkoutUrl) {
                    if (data.transactionId) {
                        sessionStorage.setItem('openpay_pending_tx', data.transactionId);
                    }
                    window.location.href = data.checkoutUrl;
                } else {
                    throw new Error('No se recibió la URL de checkout de Openpay.');
                }
            } else if (data.checkoutUrl) {
                window.location.href = data.checkoutUrl;
            } else {
                throw new Error('No se recibió la URL de pago de la pasarela');
            }
        } catch (err) {
            console.error('Error generating checkout:', err);
            setLoading(false);
            showFriendlyPaymentError(err.message || 'Error al iniciar la pasarela de pago');
        }
    };

    // Cargar datos iniciales
    useEffect(() => {
        fetchPaymentsData();
    }, []);

    // Abrir automáticamente el diálogo si viene de la redirección "Pagar Ahora" o "Abonar por Adelantado"
    useEffect(() => {
        if (loading) return;

        const hasAutoOpenIntent = location.state?.isAdvance || location.state?.invoiceId || location.state?.autoOpenPayment;
        if (!hasAutoOpenIntent) return;

        if (location.state?.isAdvance) {
            handleOpenAdvancePaymentDialog();
            navigate(location.pathname, { replace: true, state: {} });
        } else if (pendingInvoices.length > 0) {
            // Filtrar facturas sin pago pendiente de verificación
            const unpaidInvoices = pendingInvoices.filter(inv => !inv.has_pending_payment);

            if (unpaidInvoices.length === 1) {
                // SI SOLO HAY 1 FACTURA O RECIBO PENDIENTE:
                const invoiceToPay = unpaidInvoices[0];
                if (invoiceToPay) {
                    handleOpenPaymentDialog(invoiceToPay);
                }
            }
            // Limpiar la navegación en React Router de inmediato
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [loading, pendingInvoices, location.state]);

    // Confirmar pago con Openpay contra la API
    const confirmOpenpayTransaction = async (transactionId, isManualCheck = false) => {
        try {
            if (isManualCheck) {
                setOpenpayStoreModal(prev => ({ ...prev, checking: true }));
            } else {
                setLoading(true);
            }
            const token = localStorage.getItem('clientToken');
            const res = await fetch('/api/client/payments/online/openpay/confirm', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ transaction_id: transactionId })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setOpenpayStoreModal(prev => ({ ...prev, open: false, checking: false }));
                showSuccessPaymentModal({
                    title: '¡Pago Exitoso con Openpay!',
                    message: 'Su pago con Openpay (BBVA) fue procesado exitosamente. El recibo ha sido cancelado y su servicio está activo.',
                    gatewayName: 'Openpay (BBVA)',
                    transactionId
                });
            } else if (res.ok && ['charge_pending', 'in_progress', 'pending'].includes(data.status)) {
                console.info('[Openpay] Checkout no completado aún:', data.status);
                if (isManualCheck) {
                    setSuccess({
                        text: 'Tu código de pago aún se encuentra pendiente de abono. Apenas completes el pago en tu App Bancaria o Agente, tu servicio se activará automáticamente.',
                        severity: 'info'
                    });
                    setTimeout(() => setSuccess(null), 9000);
                }
            } else {
                showFriendlyPaymentError(data.message || 'El pago no fue aprobado por el banco emisor.');
            }
        } catch (err) {
            showFriendlyPaymentError('No se pudo confirmar el pago con Openpay. Si se realizó el cargo, se reflejará en unos minutos.');
        } finally {
            setOpenpayStoreModal(prev => ({ ...prev, checking: false }));
            fetchPaymentsData();
        }
    };

    // Procesar retorno del checkout oficial de Openpay (Flujo Redirect Oficial como manda Openpay)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const idParam = searchParams.get('id');
        const storedTx = sessionStorage.getItem('openpay_pending_tx');
        const transactionId = (idParam && /^tr[a-z0-9]+$/i.test(idParam)) ? idParam : storedTx;
        if (!transactionId) return;

        sessionStorage.removeItem('openpay_pending_tx');
        if (idParam) navigate(location.pathname, { replace: true });

        confirmOpenpayTransaction(transactionId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Procesar retorno de pasarela de pago (Mercado Pago back_urls)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const paymentParam = searchParams.get('payment');
        const mpStatus = searchParams.get('status');
        const paymentId = searchParams.get('payment_id') || searchParams.get('collection_id');

        if (paymentParam === 'success' || mpStatus === 'approved') {
            setSuccess({
                text: `¡Pago procesado con éxito! ${paymentId ? `(ID de Transacción: ${paymentId})` : ''} Tu servicio se actualizará automáticamente.`,
                severity: 'success'
            });
            fetchPaymentsData();
            if (refreshClientStatus) refreshClientStatus();
            window.history.replaceState({}, document.title, window.location.pathname);
            setTimeout(() => setSuccess(null), 12000);
        } else if (paymentParam === 'failure' || mpStatus === 'rejected') {
            setError('El pago fue rechazado o cancelado en la pasarela. Puedes intentar nuevamente con otro medio de pago.');
            window.history.replaceState({}, document.title, window.location.pathname);
            setTimeout(() => setError(null), 10000);
        } else if (paymentParam === 'pending' || mpStatus === 'in_process' || mpStatus === 'pending') {
            setSuccess({
                text: 'Tu pago se encuentra en proceso de verificación por la pasarela. Se activará automáticamente al confirmarse.',
                severity: 'info'
            });
            window.history.replaceState({}, document.title, window.location.pathname);
            setTimeout(() => setSuccess(null), 10000);
        }
    }, [location.search]);

    const fetchPaymentsData = async () => {
        try {
            if (pendingInvoices.length === 0 && payments.length === 0) {
                setLoading(true);
            }
            await Promise.all([
                fetchPaymentHistory(),
                fetchPendingInvoices(),
                fetchPaymentMethods()
            ]);
        } catch (err) {
            console.error('Error fetching payments data:', err);
            setError('Error al cargar datos de pagos');
        } finally {
            setLoading(false);
        }
    };

    const fetchPaymentHistory = async () => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/payments/history', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al obtener historial de pagos');
            }

            const data = await response.json();
            setPayments(data);
        } catch (err) {
            console.error('Error fetching payment history:', err);
            throw err;
        }
    };

    const fetchPendingInvoices = async () => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/invoices/pending/list', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al obtener facturas pendientes');
            }

            const data = await response.json();
            // Ordenar por due_date ascendente (oldest first) y por número de factura si coinciden
            const sortedInvoices = data.sort((a, b) => {
                const dateA = parseLocalDate(a.due_date);
                const dateB = parseLocalDate(b.due_date);
                if (dateA.getTime() !== dateB.getTime()) {
                    return dateA - dateB;
                }
                const numA = parseInt(a.invoice_number, 10) || 0;
                const numB = parseInt(b.invoice_number, 10) || 0;
                return numA - numB;
            });
            setPendingInvoices(sortedInvoices);
            // Pre-marcar todas las que no tengan ya un pago pendiente reportado en verificación
            setCheckedInvoiceIds(sortedInvoices.filter(i => !i.has_pending_payment).map(i => i.id));

            // Obtener también todas las facturas para asegurar que se muestre el estado más actualizado
            const invoicesResponse = await fetch('/api/client/invoices', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (invoicesResponse.ok) {
                // No necesitamos procesar esta respuesta, solo asegurarnos de que se recargue
                console.log('Facturas actualizadas correctamente');
            }
        } catch (err) {
            console.error('Error fetching pending invoices:', err);
            throw err;
        }
    };

    const fetchPaymentMethods = async () => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/payments/methods', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al obtener métodos de pago');
            }

            const data = await response.json();
            setPaymentMethods(data);
        } catch (err) {
            console.error('Error fetching payment methods:', err);
            throw err;
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Actualizar la función handleOpenPaymentDialog
    const handleOpenPaymentDialog = async (invoice) => {
        try {
            // Configurar los datos de forma inmediata con la factura conocida
            const initialTotal = parseFloat(invoice.amount || 0);
            const initialPaid = parseFloat(invoice.paid_amount || 0);
            const initialPending = initialTotal - initialPaid;

            setSelectedInvoice([invoice]);
            setPaymentDetails({
                invoice_id: invoice.id,
                amount: initialPending > 0 ? initialPending : 0,
                payment_method: '',
                payment_reference: '',
                proof_image_url: ''
            });
            setFileUpload(null);

            // Abrir el diálogo correspondiente inmediatamente
            const hasOnlineMethod = paymentMethods.some(method => method.is_online && method.visible_to_client);
            if (hasOnlineMethod) {
                setOpenPaymentSelectionDialog(true);
            } else {
                setOpenPaymentDialog(true);
            }

            // Obtener información más detallada en segundo plano si fuera necesario
            const token = localStorage.getItem('clientToken');
            const response = await fetch(`/api/client/invoices/${invoice.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const updatedInvoice = await response.json();
                const totalAmount = parseFloat(updatedInvoice.amount || 0);
                const paidAmount = parseFloat(updatedInvoice.paid_amount || 0);
                const pendingAmount = totalAmount - paidAmount;

                setSelectedInvoice([updatedInvoice]);
                setPaymentDetails(prev => ({
                    ...prev,
                    amount: pendingAmount > 0 ? pendingAmount : 0
                }));
            }
        } catch (error) {
            console.error('Error loading invoice details:', error);
        }
    };

    const handleClosePaymentDialog = () => {
        setOpenPaymentDialog(false);
        setSelectedInvoice(null);
        setOcrAnalyzing(false);
        setOcrStatus(null);
        setOcrMessage('');
        setOcrFilledFields({ amount: false, reference: false, method: false });
    };

    const handleOpenAdvancePaymentDialog = () => {
        setSelectedInvoice(null);
        setPaymentDetails({
            invoice_id: '',
            amount: 0,
            payment_method: '',
            payment_reference: '',
            proof_image_url: ''
        });
        setFileUpload(null);
        const hasOnlineMethod = paymentMethods.some(method => method.is_online && method.visible_to_client);
        if (hasOnlineMethod) {
            setOpenPaymentSelectionDialog(true);
        } else {
            setOpenPaymentDialog(true);
        }
    };

    const handlePaymentDetailChange = (e) => {
        const { name, value, type, checked } = e.target;
        setPaymentDetails({
            ...paymentDetails,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    // Función utilitaria para comprimir imágenes usando HTML5 Canvas
    const compressImage = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    // Ajustar dimensiones proporcionalmente a un máximo de 1000px
                    const MAX_WIDTH = 1000;
                    const MAX_HEIGHT = 1000;

                    if (width > height) {
                        if (width > MAX_WIDTH) {
                            height = Math.round((height * MAX_WIDTH) / width);
                            width = MAX_WIDTH;
                        }
                    } else {
                        if (height > MAX_HEIGHT) {
                            width = Math.round((width * MAX_HEIGHT) / height);
                            height = MAX_HEIGHT;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    // Exportar como blob JPEG con calidad del 75%
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const compressedFile = new File([blob], file.name, {
                                type: 'image/jpeg',
                                lastModified: Date.now()
                            });
                            resolve(compressedFile);
                        } else {
                            reject(new Error('Error al comprimir la imagen'));
                        }
                    }, 'image/jpeg', 0.75);
                };
                img.onerror = (err) => reject(new Error('Error al cargar la imagen en memoria'));
            };
            reader.onerror = (err) => reject(new Error('Error al leer el archivo'));
        });
    };

    const processOcrAndUpload = async (file) => {
        if (!file) return;

        setOcrAnalyzing(true);
        setOcrStatus(null);
        setOcrMessage('');
        setOcrFilledFields({ amount: false, reference: false, method: false });

        try {
            const token = localStorage.getItem('clientToken');

            // 1. Comprimir la imagen antes de subirla
            let compressedFile = file;
            try {
                compressedFile = await compressImage(file);
                console.log('✅ Imagen comprimida. Tamaño original:', file.size, 'Comprimido:', compressedFile.size);
            } catch (compressError) {
                console.warn('⚠️ Error al comprimir, usando original:', compressError);
            }

            // 2. Subir y analizar con OCR local en el backend
            const formData = new FormData();
            formData.append('proof', compressedFile);

            const response = await fetch('/api/client/payments/upload', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Error al subir el comprobante.');
            }

            const data = await response.json();
            console.log('[OCR Frontend Result]', data);

            // 3. Guardar URL e información
            setPaymentDetails(prev => ({
                ...prev,
                proof_image_url: data.proof_image_url
            }));

            const ocr = data.ocr || {};
            if (ocr.success) {
                // Auto-llenar campos
                const updatedDetails = { 
                    ...paymentDetails, 
                    proof_image_url: data.proof_image_url 
                };
                const filled = { amount: false, reference: false, method: false };

                if (ocr.amount) {
                    updatedDetails.amount = ocr.amount;
                    filled.amount = true;
                }

                if (ocr.reference) {
                    updatedDetails.payment_reference = ocr.reference;
                    filled.reference = true;
                }

                // Intentar emparejar el método detectado con los IDs disponibles
                if (ocr.method && ocr.method !== 'unknown') {
                    const cleanOcrMethod = ocr.method.toLowerCase();
                    const foundMethod = paymentMethods.find(m => {
                        const cleanName = m.name.toLowerCase();
                        return cleanName.includes(cleanOcrMethod) || 
                               (cleanOcrMethod === 'yape' && cleanName.includes('yape')) ||
                               (cleanOcrMethod === 'plin' && cleanName.includes('plin')) ||
                               (cleanOcrMethod === 'bcp' && (cleanName.includes('bcp') || cleanName.includes('crédito'))) ||
                               (cleanOcrMethod === 'bbva' && (cleanName.includes('bbva') || cleanName.includes('continental'))) ||
                               (cleanOcrMethod === 'interbank' && cleanName.includes('interbank'));
                    });

                    if (foundMethod) {
                        updatedDetails.payment_method = foundMethod.id;
                        filled.method = true;
                    }
                }

                setPaymentDetails(updatedDetails);
                setOcrStatus('success');
                setOcrMessage('¡Comprobante escaneado con éxito! Se han autocompletado los campos.');
                setOcrFilledFields(filled);
            } else {
                setOcrStatus('failed');
                setOcrMessage('No pudimos leer los datos automáticamente. Por favor completa los campos a mano para enviarlo a revisión manual.');
                setOcrFilledFields({ amount: false, reference: false, method: false });
            }
        } catch (err) {
            console.error('[OCR Frontend Error]', err);
            setOcrStatus('failed');
            setOcrMessage(err.message || 'Error al procesar el comprobante.');
            setOcrFilledFields({ amount: false, reference: false, method: false });
        } finally {
            setOcrAnalyzing(false);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            setFileUpload(selectedFile);

            // Generamos una URL blob local temporal solo para mostrar la previsualización en la UI
            const fileUrl = URL.createObjectURL(selectedFile);
            setPaymentDetails({
                ...paymentDetails,
                proof_image_url: fileUrl
            });

            // Disparar procesamiento OCR inmediato
            processOcrAndUpload(selectedFile);
        }
    };

    // Envío del pago
    const handleSubmitPayment = async () => {
        try {
            setSubmitting(true);
            const token = localStorage.getItem('clientToken');
            let isAutoApproved = false;

            let targetInvoiceId = null;
            const targetAmount = parseFloat(paymentDetails.amount);

            if (selectedInvoice) {
                const invoices = Array.isArray(selectedInvoice) ? selectedInvoice : [selectedInvoice];
                if (invoices.length === 1) {
                    const inv = invoices[0];
                    const pendingAmount = parseFloat(inv.amount) - parseFloat(inv.paid_amount || 0);
                    // Si es una sola factura y el pago no excede su deuda, se asocia directamente.
                    // Si excede la deuda de la factura, se envía como abono general (invoice_id = null) para conciliar y dejar el sobrante como saldo a favor.
                    if (targetAmount <= pendingAmount) {
                        targetInvoiceId = inv.id;
                    }
                }
            }

            let notes = '';
            if (selectedInvoice) {
                const invoices = Array.isArray(selectedInvoice) ? selectedInvoice : [selectedInvoice];
                if (invoices.length > 0) {
                    const numbers = invoices.map(i => `#${i.invoice_number}`).join(', ');
                    notes = invoices.length === 1 ? `Pago para factura: ${numbers}` : `Pago agrupado para: ${numbers}`;
                    if (invoices.length > 1 && requestMerge) {
                        notes += ' (Solicita comprobante unificado)';
                    }
                }
            } else {
                notes = 'Abono por adelantado (Saldo a favor)';
            }

            const paymentPayload = {
                invoice_id: targetInvoiceId,
                amount: targetAmount,
                payment_method: paymentDetails.payment_method,
                payment_reference: paymentDetails.payment_reference,
                proof_image_url: paymentDetails.proof_image_url,
                notes: notes
            };

            const response = await fetch('/api/client/payments', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(paymentPayload)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Error al registrar el pago');
            }

            const resData = await response.json();
            isAutoApproved = resData.auto_approved;

            // Cerrar diálogo y refrescar datos
            handleClosePaymentDialog();
            await fetchPaymentsData();
            if (refreshClientStatus) {
                console.log('[Frontend Payment Success] Refrescando estado de sesión del cliente...');
                await refreshClientStatus();
            }

            if (isAutoApproved && !targetInvoiceId) {
                // Caso de éxito total de abono libre o excedente: Mostrar popup Premium y redirigir
                setAutoApprovedAdvanceData({
                    amount: targetAmount,
                    newBalance: resData.new_balance !== undefined ? parseFloat(resData.new_balance) : (parseFloat(client?.balance || 0) + targetAmount)
                });
                setOpenAutoApprovedAdvanceModal(true);
            } else {
                if (isAutoApproved) {
                    setSuccess({
                        text: '¡Pago verificado con éxito! Tu abono ha sido procesado de forma automática y tu servicio se encuentra activo.',
                        severity: 'success'
                    });
                } else {
                    setSuccess({
                        text: '¡Pago registrado con éxito! Tu reporte ha sido enviado a la administración para revisión y verificación manual.',
                        severity: 'info' // Color azul informativo y amigable
                    });
                }
                // Mostrar durante 15 segundos para que se pueda leer por completo
                setTimeout(() => setSuccess(null), 15000);
            }
        } catch (err) {
            console.error('Error submitting payment:', err);
            setError(err.message || 'Error al registrar el pago');

            // Limpiar mensaje de error después de 5 segundos
            setTimeout(() => setError(null), 5000);
        } finally {
            setSubmitting(false);
        }
    };

    const handleViewPaymentDetails = async (paymentId) => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch(`/api/client/payments/${paymentId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al obtener detalles del pago');
            }

            const data = await response.json();
            setSelectedPayment(data);
            setOpenPaymentDetailDialog(true);
        } catch (err) {
            console.error('Error fetching payment details:', err);
            setError(err.message || 'Error al obtener detalles del pago');

            // Limpiar mensaje de error después de 5 segundos
            setTimeout(() => setError(null), 5000);
        }
    };

    const handleClosePaymentDetailDialog = () => {
        setOpenPaymentDetailDialog(false);
        setSelectedPayment(null);
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'completed':
                return <Chip label="Completado" color="success" size="small" icon={<CheckIcon />} />;
            case 'pending':
                return <Chip label="Pendiente" color="primary" size="small" />;
            case 'rejected':
                return <Chip label="Rechazado" color="error" size="small" icon={<CloseIcon />} />;
            // Mantener compatibilidad con datos antiguos que pudieran tener 'approved'
            case 'approved':
                return <Chip label="Completado" color="success" size="small" icon={<CheckIcon />} />;
            default:
                return <Chip label={status} size="small" />;
        }
    };

    if (loading) {
        return (
            <FiberLoader text="Conectando módulo de pagos..." subtext="Cargando historial y comprobantes por Fibra Óptica..." fullPage />
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 4 }, maxWidth: '100%', overflowX: 'hidden' }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    <PaymentIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                    Recibos
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    Gestiona tus recibos y pagos pendientes
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert 
                    severity={typeof success === 'object' ? success.severity : 'success'} 
                    sx={{ mb: 3 }}
                    onClose={() => setSuccess(null)}
                >
                    {typeof success === 'object' ? success.text : success}
                </Alert>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab icon={<MoneyIcon />} iconPosition="start" label="Pendientes" />
                    <Tab icon={<HistoryIcon />} iconPosition="start" label="Pagados" />
                </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.secondary', fontFamily: '"Outfit", sans-serif' }}>
                        Recibos Pendientes
                    </Typography>
                    <Button
                        variant="outlined"
                        color="success"
                        startIcon={<MoneyIcon />}
                        onClick={handleOpenAdvancePaymentDialog}
                        sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 'bold', fontSize: '0.85rem' }}
                    >
                        Reportar Pago por Adelantado
                    </Button>
                </Box>

                {pendingInvoices.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)' }}>
                        <ReceiptIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                            No tienes recibos pendientes
                        </Typography>
                        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                            Tu cuenta está al día. ¡Gracias por tu puntualidad!
                        </Typography>
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<MoneyIcon />}
                            onClick={handleOpenAdvancePaymentDialog}
                            sx={{ borderRadius: 2.5, px: 4, py: 1.2, textTransform: 'none', fontWeight: 'bold' }}
                        >
                            Reportar Pago por Adelantado (Saldo a Favor)
                        </Button>
                    </Paper>
                ) : (
                    <Box>
                        {/* Lista compacta de facturas pendientes */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {pendingInvoices.map((invoice, idx) => {
                                const isChecked = checkedInvoiceIds.includes(invoice.id);
                                const isOverdue = parseLocalDate(invoice.due_date) < new Date();
                                const pendingAmount = parseFloat(invoice.amount) - parseFloat(invoice.paid_amount || 0);

                                return (
                                    <Paper 
                                        key={invoice.id}
                                        elevation={isChecked ? 2 : 1}
                                        sx={{ 
                                            p: 2, 
                                            display: 'flex', 
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            alignItems: { xs: 'flex-start', sm: 'center' }, 
                                            justifyContent: 'space-between',
                                            borderRadius: 3, 
                                            border: isChecked ? '1px solid #1565c0' : '1px solid rgba(0,0,0,0.06)',
                                            bgcolor: isChecked ? 'rgba(21, 101, 192, 0.02)' : 'background.paper',
                                            transition: 'all 0.2s ease',
                                            gap: { xs: 1.5, sm: 2 },
                                            '&:hover': {
                                                borderColor: isChecked ? 'primary.main' : 'grey.400',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                            }
                                        }}
                                    >
                                        {/* 1. Recibo y Descarga */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: { xs: '100%', sm: '180px' }, justifyContent: 'space-between' }}>
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                                                        Recibo #{invoice.invoice_number}
                                                    </Typography>
                                                    <IconButton 
                                                        size="small" 
                                                        color="primary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenPdfModal(invoice.id, 'ticket');
                                                        }}
                                                        title="Ver Detalles del Recibo"
                                                        sx={{ p: 0.5 }}
                                                    >
                                                        <ViewIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                    <IconButton 
                                                        size="small" 
                                                        color="primary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDownloadInvoice(invoice.id);
                                                        }}
                                                        title="Descargar Recibo"
                                                        sx={{ p: 0.5 }}
                                                    >
                                                        <DownloadIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Vence: {format(parseLocalDate(invoice.due_date), 'dd/MM/yyyy')}
                                                </Typography>
                                            </Box>
                                            
                                            {/* Checkbox visible en móvil al lado del título */}
                                            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    disabled={invoice.has_pending_payment}
                                                    onChange={(e) => handleCheckboxChange(invoice.id, e.target.checked)}
                                                    style={{ 
                                                        width: '20px', 
                                                        height: '20px', 
                                                        cursor: invoice.has_pending_payment ? 'not-allowed' : 'pointer',
                                                        accentColor: '#1565c0'
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* 2. Estado (Chip simple) */}
                                        <Box sx={{ display: { xs: 'block', sm: 'block' } }}>
                                            {isOverdue ? (
                                                <Chip label="Vencida" color="error" size="small" sx={{ fontWeight: 'bold', height: 22, fontSize: '0.7rem' }} />
                                            ) : (
                                                <Chip label="Pendiente" color="primary" size="small" sx={{ fontWeight: 'bold', height: 22, fontSize: '0.7rem' }} />
                                            )}
                                        </Box>

                                        {/* 3. Montos (Detalle completo) */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'space-between', width: { xs: '100%', sm: 'auto' }, borderTop: { xs: '1px solid rgba(0,0,0,0.04)', sm: 'none' }, pt: { xs: 1, sm: 0 } }}>
                                            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                    Monto Total
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                                                    S/ {parseFloat(invoice.amount).toFixed(2)}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                    Pagado
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                                                    S/ {parseFloat(invoice.paid_amount || 0).toFixed(2)}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', fontWeight: 'bold' }}>
                                                    Pendiente
                                                </Typography>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'error.main', fontSize: '1rem' }}>
                                                    S/ {pendingAmount.toFixed(2)}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* 4. Estado de Pago Reportado / Alerta */}
                                        <Box sx={{ display: 'flex', justifyContent: 'center', width: { xs: '100%', sm: 'auto' } }}>
                                            {invoice.has_pending_payment ? (
                                                <Chip 
                                                    label="Pago en Verificación" 
                                                    color="warning" 
                                                    size="small" 
                                                    icon={<WarningIcon sx={{ fontSize: '14px !important' }} />}
                                                    sx={{ fontWeight: '500', height: 24, fontSize: '0.75rem', bgcolor: isDarkMode ? 'rgba(245, 158, 11, 0.15)' : '#fff9e6', color: isDarkMode ? '#f59e0b' : '#856404', border: '1px solid', borderColor: isDarkMode ? 'rgba(245, 158, 11, 0.3)' : '#ffeeba', width: '100%' }} 
                                                />
                                            ) : isChecked ? (
                                                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold', display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                                                    <CheckIcon fontSize="small" /> Seleccionada
                                                </Typography>
                                            ) : null}
                                        </Box>

                                        {/* 5. Checkbox (Far Right) en PC */}
                                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', pl: 1 }}>
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                disabled={invoice.has_pending_payment}
                                                onChange={(e) => handleCheckboxChange(invoice.id, e.target.checked)}
                                                style={{ 
                                                    width: '20px', 
                                                    height: '20px', 
                                                    cursor: invoice.has_pending_payment ? 'not-allowed' : 'pointer',
                                                    accentColor: '#1565c0'
                                                }}
                                            />
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>

                        {/* Bottom Action Bar */}
                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                            <Paper 
                                elevation={3} 
                                sx={{ 
                                    p: 2.5, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between', 
                                    bgcolor: 'rgba(21, 101, 192, 0.04)', 
                                    borderRadius: 3, 
                                    border: '1px solid rgba(21, 101, 192, 0.15)',
                                    width: '100%',
                                    maxWidth: { xs: '100%', sm: '480px' },
                                    gap: 3,
                                    boxShadow: '0 8px 30px rgba(0,0,0,0.06)'
                                }}
                            >
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: 0.5, display: 'block' }}>
                                        Total a Pagar ({checkedInvoiceIds.length} select.)
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main', mt: 0.2 }}>
                                        S/ {
                                            pendingInvoices
                                                .filter(i => checkedInvoiceIds.includes(i.id))
                                                .reduce((sum, i) => sum + (parseFloat(i.amount) - parseFloat(i.paid_amount || 0)), 0)
                                                .toFixed(2)
                                        }
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<PaymentIcon />}
                                    disabled={checkedInvoiceIds.length === 0}
                                    onClick={handleOpenGroupPaymentDialog}
                                    sx={{ 
                                        borderRadius: 2.5, 
                                        px: 4, 
                                        py: 1.2, 
                                        textTransform: 'none', 
                                        fontWeight: 'bold', 
                                        boxShadow: '0 4px 12px rgba(21, 101, 192, 0.2)' 
                                    }}
                                >
                                    {checkedInvoiceIds.length > 1 ? 'Pagar Recibos' : 'Pagar Recibo'}
                                </Button>
                            </Paper>
                        </Box>
                    </Box>
                )}

                {/* Sub-sección: Reportes de Pago en Verificación */}
                {payments.filter(p => p.status === 'pending').length > 0 && (
                    <Box sx={{ mt: 5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'warning.dark', fontFamily: '"Outfit", sans-serif', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarningIcon sx={{ color: 'warning.main', fontSize: '1.2rem' }} />
                            Mis Reportes de Pago en Verificación
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {payments.filter(p => p.status === 'pending').map((payment) => {
                                const dateToUse = payment.invoice_due_date || payment.invoice_created_at || payment.payment_date;
                                const labelText = payment.invoice_number && payment.invoice_number !== 'SALDO-ADELANTADO'
                                    ? `Abono a Recibo #${payment.invoice_number}`
                                    : 'Abono de Saldo por Adelantado';
                                
                                return (
                                    <Paper 
                                        key={payment.id}
                                        elevation={1}
                                        sx={{ 
                                            p: 2, 
                                            display: 'flex', 
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            alignItems: { xs: 'flex-start', sm: 'center' }, 
                                            justifyContent: 'space-between',
                                            borderRadius: 3, 
                                            border: '1px solid rgba(245, 158, 11, 0.15)',
                                            bgcolor: 'rgba(245, 158, 11, 0.01)',
                                            gap: { xs: 1.5, sm: 2 }
                                        }}
                                    >
                                        <Box sx={{ minWidth: { xs: '100%', sm: '200px' } }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                                {labelText}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Reportado el: {format(new Date(payment.payment_date), 'dd/MM/yyyy HH:mm')}
                                            </Typography>
                                        </Box>
                                        
                                        <Box>
                                            <Chip 
                                                label="En Verificación Manual" 
                                                color="warning" 
                                                size="small" 
                                                sx={{ fontWeight: '600', bgcolor: isDarkMode ? 'rgba(245, 158, 11, 0.15)' : '#fff9e6', color: isDarkMode ? '#f59e0b' : '#856404', border: '1px solid', borderColor: isDarkMode ? 'rgba(245, 158, 11, 0.3)' : '#ffeeba' }} 
                                            />
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'space-between', width: { xs: '100%', sm: 'auto' } }}>
                                            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                    Monto
                                                </Typography>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
                                                    S/ {parseFloat(payment.amount || 0).toFixed(2)}
                                                </Typography>
                                            </Box>
                                            
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                    Referencia
                                                </Typography>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                                                    {payment.payment_reference || 'S/R'}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: { xs: '100%', sm: 'auto' } }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="warning"
                                                onClick={() => handleViewPaymentDetails(payment.id)}
                                                startIcon={<ViewIcon sx={{ fontSize: '1rem !important' }} />}
                                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                            >
                                                Detalles
                                            </Button>
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                {payments.filter(p => p.status === 'completed' || p.status === 'approved').length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <HistoryIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            No hay historial de pagos aprobados
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                            Aún no tienes pagos aprobados en el sistema.
                        </Typography>
                    </Paper>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {payments.filter(p => p.status === 'completed' || p.status === 'approved').map((payment) => {
                            const dateToUse = payment.invoice_due_date || payment.invoice_created_at || payment.payment_date;
                            const invoiceMonth = getInvoiceMonthName(dateToUse);
                            const invoiceAmount = parseFloat(payment.invoice_amount || payment.amount || 0);
                            const paidAmount = parseFloat(payment.amount || 0);

                            return (
                                <Paper 
                                    key={payment.id}
                                    elevation={1}
                                    sx={{ 
                                        p: 2, 
                                        display: 'flex', 
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: { xs: 'flex-start', sm: 'center' }, 
                                        justifyContent: 'space-between',
                                        borderRadius: 3, 
                                        border: '1px solid rgba(0,0,0,0.06)',
                                        bgcolor: 'background.paper',
                                        transition: 'all 0.2s ease',
                                        gap: { xs: 1.5, sm: 2 },
                                        '&:hover': {
                                            borderColor: 'primary.light',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                        }
                                    }}
                                >
                                    {/* 1. Recibo y Descarga */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: { xs: '100%', sm: '220px' }, justifyContent: 'space-between' }}>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.95rem', color: 'text.primary' }}>
                                                    {invoiceMonth}
                                                </Typography>
                                                <IconButton 
                                                     size="small" 
                                                     color="primary"
                                                     onClick={(e) => {
                                                         e.stopPropagation();
                                                         handleOpenPdfModal(payment.invoice_id, 'ticket');
                                                     }}
                                                     title="Ver / Descargar Comprobante PDF"
                                                     sx={{ p: 0.5 }}
                                                 >
                                                     <DownloadIcon sx={{ fontSize: 18 }} />
                                                 </IconButton>
                                             </Box>
                                             <Box display="flex" alignItems="center" gap={0.5}>
                                                 <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                     {payment.sunat_status === 'ACEPTADO' && payment.sunat_number
                                                         ? payment.sunat_number
                                                         : `Recibo #${payment.invoice_number}`}
                                                 </Typography>
                                                 {payment.sunat_status === 'ACEPTADO' && payment.sunat_number && (
                                                     <Chip
                                                         label={payment.sunat_number.startsWith('F') ? 'Factura SUNAT' : 'Boleta SUNAT'}
                                                         color="info"
                                                         size="small"
                                                         sx={{ fontWeight: 700, fontSize: '0.65rem', height: '18px' }}
                                                     />
                                                 )}
                                             </Box>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Pagado el: {format(new Date(payment.payment_date), 'dd/MM/yyyy HH:mm')}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* 2. Estado (Chip simple) */}
                                    <Box>
                                        {getStatusChip(payment.status)}
                                    </Box>

                                    {/* 3. Montos (Detalle completo) */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'space-between', width: { xs: '100%', sm: 'auto' }, borderTop: { xs: '1px solid rgba(0,0,0,0.04)', sm: 'none' }, pt: { xs: 1, sm: 0 } }}>
                                        <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                Monto Total
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                                                S/ {invoiceAmount.toFixed(2)}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                Monto Pagado
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                                S/ {paidAmount.toFixed(2)}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                Referencia
                                            </Typography>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', fontFamily: 'monospace' }}>
                                                {payment.payment_reference || 'S/R'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* 4. Método de Pago */}
                                    <Box sx={{ minWidth: { xs: '100%', sm: '120px' }, textAlign: { xs: 'left', sm: 'center' } }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'inline', sm: 'block' }, fontSize: '0.7rem', mr: 1 }}>
                                            Método:
                                        </Typography>
                                        <Typography variant="body2" sx={{ display: { xs: 'inline', sm: 'block' }, fontWeight: 600, color: 'text.primary' }}>
                                            {payment.payment_method_name}
                                        </Typography>
                                    </Box>

                                    {/* 5. Botón de Acción (Ojo / Detalles) */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: { xs: '100%', sm: 'auto' } }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleViewPaymentDetails(payment.id)}
                                            startIcon={<ViewIcon sx={{ fontSize: '1rem !important' }} />}
                                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                        >
                                            Detalles
                                        </Button>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                )}
            </TabPanel>

            <Dialog open={openPaymentDialog} onClose={handleClosePaymentDialog} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 'bold' }}>Registrar Reporte de Pago</DialogTitle>
                <DialogContent>
                    {(selectedInvoice === null || (selectedInvoice && selectedInvoice.length > 0)) && (
                        <Box sx={{ p: 1 }}>
                            {error && (
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    {error}
                                </Alert>
                            )}

                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                                {!selectedInvoice 
                                    ? 'Reportar Pago por Adelantado (Abono a tu Saldo a Favor)'
                                    : selectedInvoice.length === 1 
                                        ? `Factura #${selectedInvoice[0].invoice_number}`
                                        : `Pago Agrupado (${selectedInvoice.length} Facturas)`
                                }
                            </Typography>

                            {!selectedInvoice ? (
                                <Box sx={{ mb: 3, p: 2, bgcolor: isDarkMode ? 'rgba(76, 175, 80, 0.1)' : 'success.50', borderRadius: 2, border: '1px dashed rgba(16, 185, 129, 0.2)' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: isDarkMode ? 'success.light' : 'success.dark', mb: 1 }}>
                                        ¿Qué es un Pago por Adelantado?
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Este abono se cargará directamente como saldo a tu favor. Se utilizará automáticamente para pagar tu próximo recibo mensual tan pronto como se genere.
                                    </Typography>
                                </Box>
                            ) : selectedInvoice.length > 1 && (
                                <Box sx={{ mb: 3, p: 2, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'grey.50', borderRadius: 2, border: '1px dashed', borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        Detalle de Facturas Seleccionadas:
                                    </Typography>
                                    <Grid container spacing={1}>
                                        {selectedInvoice.map((inv) => (
                                            <Grid item xs={12} sm={6} key={inv.id}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    • Factura <strong>#{inv.invoice_number}</strong>: S/ {(parseFloat(inv.amount) - parseFloat(inv.paid_amount || 0)).toFixed(2)}
                                                </Typography>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}

                            {selectedInvoice && selectedInvoice.length > 1 && (
                                <Box sx={{ mb: 2, mt: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={requestMerge}
                                                onChange={(e) => setRequestMerge(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                Solicitar un único comprobante unificado (Boleta/Factura/Recibo) para todo este pago
                                            </Typography>
                                        }
                                    />
                                </Box>
                            )}

                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid item xs={12} md={!selectedInvoice ? 12 : (selectedInvoice.length === 1 ? 6 : 12)}>
                                    <Card variant="outlined" sx={{ borderRadius: 3, borderColor: 'primary.light', bgcolor: 'rgba(21, 101, 192, 0.02)' }}>
                                        <CardContent>
                                            <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'medium' }}>
                                                {!selectedInvoice ? 'Monto a Adelantar (Soles S/)' : (selectedInvoice.length === 1 ? 'Monto Pendiente' : 'Monto Acumulado Pendiente')}
                                            </Typography>
                                            <Typography variant="h5" color="primary.main" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                                                S/ {paymentDetails.amount ? parseFloat(paymentDetails.amount).toFixed(2) : '0.00'}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                {selectedInvoice && selectedInvoice.length === 1 && (
                                    <Grid item xs={12} md={6}>
                                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                            <CardContent>
                                                <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'medium' }}>
                                                    Vencimiento
                                                </Typography>
                                                <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                                                    {format(parseLocalDate(selectedInvoice[0].due_date), 'dd/MM/yyyy')}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}
                            </Grid>

                            {/* ALERTA DE PORTAL CAUTIVO SI SE DETECTA WEBVIEW RESTRINGIDO */}
                            {isWebViewRestricted && (
                                <Alert 
                                    severity="warning" 
                                    icon={<WarningIcon />}
                                    sx={{ 
                                        mb: 3, 
                                        borderRadius: 2.5,
                                        border: '1px solid',
                                        borderColor: 'warning.light',
                                        '& .MuiAlert-message': { width: '100%' }
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, fontFamily: '"Outfit", sans-serif' }}>
                                        Navegador de Portal Cautivo Limitado
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.82rem', lineHeight: 1.4, color: 'text.secondary' }}>
                                        Estás navegando desde el inicio de sesión rápido de Android. Por restricciones del sistema, <strong>no permite cargar fotos ni archivos</strong>.
                                        <br /><br />
                                        <strong>Sigue estos pasos para subir tu comprobante:</strong>
                                        <ol style={{ margin: '6px 0 0 0', paddingLeft: '18px' }}>
                                            <li style={{ marginBottom: '4px' }}>
                                                Presiona los <strong>tres puntos (⋮)</strong> arriba a la derecha y selecciona <strong>"Utilizar esta red tal cual"</strong> (o "Mantener conexión").
                                            </li>
                                            <li>
                                                Sal a la pantalla principal de tu celular, abre <strong>Google Chrome</strong> (o tu navegador preferido) e ingresa a: <strong style={{ color: '#1565c0' }}>cliente.yinowisp.com</strong> para reportar tu pago sin restricciones.
                                            </li>
                                        </ol>
                                    </Typography>
                                </Alert>
                            )}

                            {/* ZONA DE CARGA DE COMPROBANTE CON LECTOR OCR */}
                            <Box sx={{ mb: 4, position: 'relative' }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 1, fontFamily: '"Outfit", sans-serif' }}>
                                    1. Adjunta tu captura de Yape, Plin o Transferencia (Lectura OCR Inteligente)
                                </Typography>
                                
                                <Card 
                                    variant="outlined" 
                                    sx={{ 
                                        border: '2px dashed',
                                        borderColor: ocrStatus === 'success' ? 'success.light' : ocrStatus === 'failed' ? 'warning.light' : 'primary.light',
                                        borderRadius: 3, 
                                        bgcolor: ocrStatus === 'success' ? 'rgba(46, 125, 50, 0.02)' : 'rgba(21, 101, 192, 0.01)',
                                        p: 3, 
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: 120,
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            bgcolor: 'rgba(21, 101, 192, 0.04)'
                                        }
                                    }}
                                    component="div"
                                    onClick={() => {
                                        if (fileInputRef.current) {
                                            fileInputRef.current.click();
                                        }
                                    }}
                                >
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={fileInputRef}
                                        style={{ display: 'none' }}
                                        onChange={handleFileChange}
                                        disabled={ocrAnalyzing}
                                    />
                                    <UploadIcon sx={{ fontSize: 40, color: ocrStatus === 'success' ? 'success.main' : 'primary.main', mb: 1 }} />
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5, fontFamily: '"Outfit", sans-serif' }}>
                                        {fileUpload ? fileUpload.name : 'Sube tu comprobante para autocompletar'}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        Compatible con Yape, Plin, BCP, BBVA e Interbank
                                    </Typography>
                                </Card>

                                {ocrAnalyzing && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.85)' : 'rgba(255, 255, 255, 0.85)',
                                        backdropFilter: 'blur(4px)',
                                        zIndex: 10,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 3,
                                        p: 2,
                                        textAlign: 'center'
                                    }}>
                                        <CircularProgress size={36} thickness={4} sx={{ mb: 1.5 }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, fontFamily: '"Outfit", sans-serif' }}>
                                            Analizando tu comprobante con tecnología local...
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Extrayendo número de operación y montos. Esto tomará 2 segundos.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {ocrStatus && (
                                <Alert 
                                    severity={ocrStatus === 'success' ? 'success' : 'warning'} 
                                    sx={{ mb: 3, borderRadius: 2 }}
                                    icon={ocrStatus === 'success' ? <CheckIcon /> : <WarningIcon />}
                                >
                                    {ocrMessage}
                                </Alert>
                            )}

                            {paymentDetails.proof_image_url && !ocrAnalyzing && (
                                <Box sx={{ mb: 3, textAlign: 'center', p: 1.5, border: '1px solid', borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', borderRadius: 2, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'grey.50' }}>
                                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1, fontWeight: 'medium' }}>
                                        Comprobante adjunto:
                                    </Typography>
                                    <img
                                        src={paymentDetails.proof_image_url}
                                        alt="Comprobante"
                                        style={{ maxWidth: '100%', maxHeight: '160px', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                                    />
                                </Box>
                            )}

                            <Divider sx={{ my: 3.5 }}>
                                <Chip label="2. Verifica y Completa los Datos" size="small" sx={{ fontWeight: 'bold', fontFamily: '"Outfit", sans-serif' }} />
                            </Divider>

                            <TextField
                                fullWidth
                                label={!selectedInvoice ? "Monto a Adelantar (S/.)" : "Monto Total a Reportar"}
                                name="amount"
                                type="number"
                                value={paymentDetails.amount}
                                onChange={handlePaymentDetailChange}
                                required
                                InputProps={{
                                    startAdornment: <Typography sx={{ mr: 1 }}>S/</Typography>,
                                    endAdornment: ocrFilledFields.amount && (
                                        <Chip 
                                            label="Leído por IA" 
                                            color="success" 
                                            size="small" 
                                            icon={<CheckIcon sx={{ fontSize: '12px !important' }} />} 
                                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold', bgcolor: 'success.main', color: 'white' }} 
                                        />
                                    )
                                }}
                                sx={{ 
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': ocrFilledFields.amount ? {
                                        '& fieldset': { borderColor: 'success.main', borderWidth: 2 },
                                        '&:hover fieldset': { borderColor: 'success.dark' },
                                    } : {}
                                }}
                            />

                            <TextField
                                fullWidth
                                select
                                label="Método de Pago"
                                name="payment_method"
                                value={paymentDetails.payment_method}
                                onChange={handlePaymentDetailChange}
                                required
                                InputProps={{
                                    endAdornment: ocrFilledFields.method && (
                                        <Chip 
                                            label="Leído por IA" 
                                            color="success" 
                                            size="small" 
                                            icon={<CheckIcon sx={{ fontSize: '12px !important' }} />} 
                                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold', mr: 2, bgcolor: 'success.main', color: 'white' }} 
                                        />
                                    )
                                }}
                                sx={{ 
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': ocrFilledFields.method ? {
                                        '& fieldset': { borderColor: 'success.main', borderWidth: 2 },
                                        '&:hover fieldset': { borderColor: 'success.dark' },
                                    } : {}
                                }}
                            >
                                <MenuItem value="">
                                    <em>Seleccione un método de pago</em>
                                </MenuItem>
                                {paymentMethods
                                    .filter(method => !method.is_online && method.visible_to_client)
                                    .map((method) => (
                                        <MenuItem key={method.id} value={method.id}>
                                            {method.name}
                                        </MenuItem>
                                    ))
                                }
                            </TextField>

                            {paymentDetails.payment_method && (
                                <>
                                    <TextField
                                        fullWidth
                                        label="Número de Referencia"
                                        name="payment_reference"
                                        value={paymentDetails.payment_reference}
                                        onChange={handlePaymentDetailChange}
                                        placeholder="Número de transacción o código de operación"
                                        InputProps={{
                                            endAdornment: ocrFilledFields.reference && (
                                                <Chip 
                                                    label="Leído por IA" 
                                                    color="success" 
                                                    size="small" 
                                                    icon={<CheckIcon sx={{ fontSize: '12px !important' }} />} 
                                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold', bgcolor: 'success.main', color: 'white' }} 
                                                />
                                            )
                                        }}
                                        sx={{ 
                                            mb: 3,
                                            '& .MuiOutlinedInput-root': ocrFilledFields.reference ? {
                                                '& fieldset': { borderColor: 'success.main', borderWidth: 2 },
                                                '&:hover fieldset': { borderColor: 'success.dark' },
                                            } : {}
                                        }}
                                    />

                                    {/* Instrucciones del método de pago seleccionado */}
                                    {paymentMethods.find(m => m.id === paymentDetails.payment_method)?.instructions && (
                                        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                Instrucciones de Pago:
                                            </Typography>
                                            <Typography variant="body2">
                                                {paymentMethods.find(m => m.id === paymentDetails.payment_method).instructions}
                                            </Typography>
                                        </Alert>
                                    )}
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2.5, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                    <Button onClick={handleClosePaymentDialog} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancelar</Button>
                    <Button
                        variant="contained"
                        color={ocrStatus === 'success' ? 'success' : 'primary'}
                        onClick={handleSubmitPayment}
                        disabled={!paymentDetails.amount || !paymentDetails.payment_method || submitting || ocrAnalyzing}
                        startIcon={submitting ? <CircularProgress size={24} color="inherit" /> : <PaymentIcon />}
                        sx={{ borderRadius: 2.5, px: 3, py: 1, textTransform: 'none', fontWeight: 'bold' }}
                    >
                        {submitting ? 'Procesando...' : ocrStatus === 'success' ? 'Confirmar y Registrar Pago Automático' : 'Registrar Pago'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo Elegante de Selección de Método de Pago */}
            <Dialog 
                open={openPaymentSelectionDialog} 
                onClose={() => setOpenPaymentSelectionDialog(false)} 
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 4, overflow: 'hidden', maxWidth: 460, boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }
                }}
            >
                {/* Header con gradiente elegante y badge de monto */}
                <Box sx={{ 
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                    color: 'white',
                    p: 2.5,
                    pb: 3,
                    textAlign: 'center',
                    position: 'relative'
                }}>
                    <IconButton 
                        size="small" 
                        onClick={() => setOpenPaymentSelectionDialog(false)} 
                        sx={{ position: 'absolute', right: 12, top: 12, color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', fontWeight: 700, display: 'block', mb: 0.5 }}>
                        Total a Cancelar
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#38bdf8', fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
                        S/ {paymentDetails.amount ? parseFloat(paymentDetails.amount).toFixed(2) : '0.00'} <Typography component="span" variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>PEN</Typography>
                    </Typography>
                    <Chip 
                        label={selectedInvoice && Array.isArray(selectedInvoice) && selectedInvoice.length === 1 ? `Recibo #${selectedInvoice[0].invoice_number}` : `${selectedInvoice?.length || 1} Recibo(s) Seleccionado(s)`}
                        size="small"
                        sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.12)', color: '#f1f5f9', fontWeight: 600, fontSize: '0.75rem' }}
                    />
                </Box>

                <DialogContent sx={{ p: 2.5, pt: 3, overflowX: 'hidden' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PaymentIcon sx={{ fontSize: 18, color: '#0284c7' }} /> Selecciona tu pasarela o medio de pago:
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={1.5} sx={{ width: '100%', overflowX: 'hidden' }}>
                        {/* Pasarelas de Pago Online (Culqi / Openpay) */}
                        {paymentMethods
                            .filter(method => method.visible_to_client && method.is_online)
                            .map((method) => {
                                const isCulqi = method.gateway_type === 'culqi';
                                const isOpenpay = method.gateway_type === 'openpay';
                                const brandColor = isCulqi ? '#0284c7' : isOpenpay ? '#004481' : '#2563eb';
                                const bgHover = isCulqi ? 'rgba(2, 132, 199, 0.04)' : isOpenpay ? 'rgba(0, 68, 129, 0.04)' : 'rgba(37, 99, 235, 0.04)';

                                return (
                                    <Paper
                                        key={method.id}
                                        elevation={0}
                                        onClick={() => handleSelectOnlinePayment(method)}
                                        sx={{
                                            p: 1.75,
                                            px: 2,
                                            borderRadius: 3,
                                            border: '1.5px solid',
                                            borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
                                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : '#ffffff',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease-in-out',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                borderColor: brandColor,
                                                bgcolor: bgHover,
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 8px 20px -4px ${brandColor}25`
                                            }
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
                                            <Box sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: 2.5,
                                                bgcolor: method.logo_url ? '#ffffff' : (isOpenpay ? '#004481' : isCulqi ? '#e0f2fe' : `${brandColor}15`),
                                                border: method.logo_url ? '1px solid #e2e8f0' : 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                p: method.logo_url ? 0.5 : 0,
                                                boxShadow: method.logo_url ? '0 2px 6px rgba(0,0,0,0.06)' : (isOpenpay ? '0 2px 8px rgba(0, 68, 129, 0.25)' : 'none')
                                            }}>
                                                {method.logo_url ? (
                                                    <img 
                                                        src={method.logo_url} 
                                                        alt={method.name} 
                                                        style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }} 
                                                    />
                                                ) : isOpenpay ? (
                                                    <BankIcon sx={{ color: '#ffffff', fontSize: 24 }} />
                                                ) : isCulqi ? (
                                                    <CardIcon sx={{ color: '#0284c7', fontSize: 24 }} />
                                                ) : (
                                                    <BoltIcon sx={{ color: brandColor, fontSize: 24 }} />
                                                )}
                                            </Box>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Box display="flex" alignItems="center" gap={1} mb={0.2} flexWrap="nowrap">
                                                    <Typography variant="body1" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.92rem', whiteSpace: 'nowrap' }}>
                                                        {method.name || (isCulqi ? 'Culqi' : isOpenpay ? 'Openpay (BBVA)' : 'Pasarela Online')}
                                                    </Typography>
                                                    <Chip label="Instantáneo" size="small" sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 800, fontSize: '0.65rem', height: 18 }} />
                                                </Box>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.75rem', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {method.description || (isCulqi ? 'Tarjetas, Yape y PagoEfectivo' : isOpenpay ? 'Tarjetas, QR Yape/Plin, BCP y Agentes' : 'Pagos en línea instantáneos')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <ChevronRightIcon sx={{ color: brandColor, ml: 1, flexShrink: 0 }} />
                                    </Paper>
                                );
                            })
                        }

                        {/* Botón para reportar manual */}
                        <Paper
                            elevation={0}
                            onClick={() => {
                                setOpenPaymentSelectionDialog(false);
                                setOpenPaymentDialog(true);
                            }}
                            sx={{
                                p: 1.75,
                                px: 2,
                                borderRadius: 3,
                                border: '1.5px dashed',
                                borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : '#cbd5e1',
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                boxSizing: 'border-box',
                                overflow: 'hidden',
                                '&:hover': {
                                    borderColor: '#10b981',
                                    bgcolor: 'rgba(16, 185, 129, 0.04)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 20px -4px rgba(16, 185, 129, 0.2)'
                                }
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
                                <Box sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 2.5,
                                    bgcolor: '#ecfdf5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <UploadIcon sx={{ color: '#059669', fontSize: 24 }} />
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Box display="flex" alignItems="center" gap={1} mb={0.2} flexWrap="nowrap">
                                        <Typography variant="body1" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.92rem', whiteSpace: 'nowrap' }}>
                                            Transferencia o Depósito Manual
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.75rem', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        Sube tu comprobante de Yape, Plin o BCP
                                    </Typography>
                                </Box>
                            </Box>
                            <ChevronRightIcon sx={{ color: '#059669', ml: 1, flexShrink: 0 }} />
                        </Paper>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Diálogo para ver detalles de un pago */}
            <Dialog open={openPaymentDetailDialog} onClose={handleClosePaymentDetailDialog} maxWidth="sm" fullWidth>
                {selectedPayment && (
                    <>
                        <DialogTitle>
                            Detalles del Pago
                            <Typography variant="subtitle2" color="textSecondary">
                                ID: {selectedPayment.id.substr(0, 8)}
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">Comprobante / Factura</Typography>
                                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                        <Typography variant="body1" fontWeight="bold">
                                            {selectedPayment.sunat_status === 'ACEPTADO' && selectedPayment.sunat_number
                                                ? selectedPayment.sunat_number
                                                : `#${selectedPayment.invoice_number || 'SALDO-ADELANTADO'}`}
                                        </Typography>
                                        {selectedPayment.sunat_status === 'ACEPTADO' && selectedPayment.sunat_number ? (
                                            <Chip
                                                label={selectedPayment.sunat_number.startsWith('F') ? 'Factura SUNAT' : 'Boleta SUNAT'}
                                                color="info"
                                                size="small"
                                                sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                                            />
                                        ) : (
                                            <Chip
                                                label="Recibo Interno"
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem' }}
                                            />
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">Estado</Typography>
                                    <Typography variant="body1">{getStatusChip(selectedPayment.status)}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">Monto</Typography>
                                    <Typography variant="body1">S/ {parseFloat(selectedPayment.amount).toFixed(2)}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">Fecha</Typography>
                                    <Typography variant="body1">
                                        {format(new Date(selectedPayment.payment_date), 'dd/MM/yyyy HH:mm')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">Método</Typography>
                                    <Typography variant="body1">{selectedPayment.payment_method_name}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">Referencia</Typography>
                                    <Typography variant="body1">{selectedPayment.payment_reference || '-'}</Typography>
                                </Grid>

                                {selectedPayment.proof_image_url && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="textSecondary" gutterBottom>Comprobante</Typography>
                                        <Box sx={{ textAlign: 'center', mt: 1 }}>
                                            <img
                                                src={selectedPayment.proof_image_url}
                                                alt="Comprobante"
                                                style={{ maxWidth: '100%', maxHeight: '300px' }}
                                            />
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between' }}>
                            {selectedPayment.invoice_id ? (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<ReceiptIcon />}
                                    onClick={() => handleOpenPdfModal(selectedPayment.invoice_id, 'ticket')}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                >
                                    📄 Ver Comprobante PDF (Ticket / A4)
                                </Button>
                            ) : <Box />}
                            <Button onClick={handleClosePaymentDetailDialog} sx={{ borderRadius: 2 }}>
                                Cerrar
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Diálogo Visor de PDF (Ticket / A4 / SUNAT) */}
            <Dialog open={pdfModal.open} onClose={() => setPdfModal({ open: false, invoiceId: null, format: 'ticket' })} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <ReceiptIcon color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                            Comprobante PDF
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setPdfModal({ open: false, invoiceId: null, format: 'ticket' })}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                        <Typography variant="body2" color="textSecondary">
                            Formato de Previsualización:
                        </Typography>
                        <ToggleButtonGroup
                            size="small"
                            value={pdfModal.format}
                            exclusive
                            onChange={(e, newFormat) => {
                                if (newFormat) setPdfModal(prev => ({ ...prev, format: newFormat }));
                            }}
                            color="primary"
                        >
                            <ToggleButton value="ticket">🎟️ Formato Ticket (80mm)</ToggleButton>
                            <ToggleButton value="a4">📄 Formato A4</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {pdfModal.invoiceId && (
                        <Box sx={{ width: '100%', height: '70vh', bgcolor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                            <iframe
                                src={`/api/client/invoices/${pdfModal.invoiceId}/download?format=${pdfModal.format}&inline=true&token=${localStorage.getItem('clientToken')}`}
                                title="Comprobante PDF"
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                            />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Dialog Premium para Pago por Adelantado Auto-Aprobado por OCR */}
            <Dialog 
                open={openAutoApprovedAdvanceModal} 
                onClose={() => {
                    setOpenAutoApprovedAdvanceModal(false);
                    navigate('/dashboard');
                }}
                maxWidth="xs" 
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 4, p: 1, textAlign: 'center' }
                }}
            >
                <DialogContent sx={{ pt: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Box sx={{
                            width: '72px',
                            height: '72px',
                            borderRadius: '50%',
                            bgcolor: 'success.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '4px solid #e8f5e9',
                        }}>
                            <CheckIcon sx={{ fontSize: 40, color: 'white' }} />
                        </Box>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, fontFamily: '"Outfit", sans-serif', color: 'success.main' }}>
                        ¡Abono Verificado! 🚀
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        Tu comprobante de pago fue verificado al instante por nuestro lector inteligente.
                    </Typography>
                    
                    <Box sx={{ bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'grey.50', p: 2, borderRadius: 3, mb: 3, border: '1px solid', borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' }}>
                           <Grid container spacing={1}>
                               <Grid item xs={12}>
                                   <Typography variant="caption" color="textSecondary" display="block">Monto Abonado</Typography>
                                   <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                       S/ {autoApprovedAdvanceData.amount.toFixed(2)}
                                   </Typography>
                               </Grid>
                               <Grid item xs={12} sx={{ mt: 1 }}>
                                   <Divider sx={{ borderStyle: 'dashed' }} />
                               </Grid>
                               <Grid item xs={12} sx={{ mt: 1 }}>
                                   <Typography variant="caption" color="textSecondary" display="block">Tu Nuevo Saldo a Favor</Typography>
                                   <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                       S/ {autoApprovedAdvanceData.newBalance.toFixed(2)}
                                   </Typography>
                               </Grid>
                           </Grid>
                    </Box>
                    
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1, fontWeight: 'medium' }}>
                        💡 Este crédito se descontará automáticamente en tu próximo recibo mensual.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={() => {
                            setOpenAutoApprovedAdvanceModal(false);
                            navigate('/dashboard');
                        }}
                        fullWidth
                        sx={{ borderRadius: 2.5, py: 1.2, fontWeight: 'bold', textTransform: 'none', fontSize: '0.95rem' }}
                    >
                        Entendido, Ir al Inicio
                    </Button>
                </DialogActions>
            </Dialog>



            {/* Modal Elegante de Error de Tarjeta */}
            <Dialog 
                open={openpayErrorModal.open} 
                onClose={() => setOpenpayErrorModal(prev => ({ ...prev, open: false }))}
                maxWidth="xs" 
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 4, p: 1, textAlign: 'center' }
                }}
            >
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Box sx={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '50%',
                            bgcolor: '#fef2f2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '4px solid #fee2e2',
                        }}>
                            <CloseIcon sx={{ fontSize: 36, color: '#ef4444' }} />
                        </Box>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, fontFamily: '"Outfit", sans-serif', color: '#991b1b' }}>
                        {openpayErrorModal.title}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#dc2626', mb: 2 }}>
                        {openpayErrorModal.message}
                    </Typography>
                    
                    <Box sx={{ bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc', p: 2, borderRadius: 3, mb: 2, border: '1px solid #e2e8f0', textAlign: 'left' }}>
                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                            💡 Sugerencia:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                            {openpayErrorModal.suggestion}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ flexDirection: 'column', gap: 1, pb: 2.5, px: 3 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => setOpenpayErrorModal(prev => ({ ...prev, open: false }))}
                        sx={{ bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' }, borderRadius: 2.5, py: 1.2, fontWeight: 'bold', textTransform: 'none' }}
                    >
                        Probar con otra Tarjeta
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        onClick={() => {
                            setOpenpayErrorModal(prev => ({ ...prev, open: false }));
                            setOpenPaymentSelectionDialog(true);
                        }}
                        sx={{ borderRadius: 2.5, py: 1, textTransform: 'none', fontWeight: 'bold', fontSize: '0.85rem' }}
                    >
                        Elegir otro método de pago
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal Elegante de Éxito de Pago */}
            <Dialog 
                open={successPaymentModal.open} 
                onClose={() => setSuccessPaymentModal(prev => ({ ...prev, open: false }))}
                maxWidth="xs" 
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 4, p: 1, textAlign: 'center' }
                }}
            >
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Box sx={{
                            width: '72px',
                            height: '72px',
                            borderRadius: '50%',
                            bgcolor: '#ecfdf5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '4px solid #a7f3d0',
                            boxShadow: '0 0 20px rgba(16, 185, 129, 0.25)'
                        }}>
                            <CheckCircleIcon sx={{ fontSize: 48, color: '#10b981' }} />
                        </Box>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, fontFamily: '"Outfit", sans-serif', color: '#065f46' }}>
                        {successPaymentModal.title}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#047857', mb: 2.5, px: 1 }}>
                        {successPaymentModal.message}
                    </Typography>
                    
                    <Box sx={{ bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc', p: 2, borderRadius: 3, mb: 1, border: '1px solid #cbd5e1', textAlign: 'left' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.2}>
                            <Typography variant="caption" color="textSecondary" fontWeight="bold">Pasarela de Pago:</Typography>
                            <Chip label={successPaymentModal.gatewayName} color="success" size="small" sx={{ fontWeight: 800, fontSize: '0.75rem' }} />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.2}>
                            <Typography variant="caption" color="textSecondary" fontWeight="bold">Estado de Servicio:</Typography>
                            <Chip label="⚡ Servicio Activo" color="primary" size="small" sx={{ fontWeight: 800, fontSize: '0.75rem' }} />
                        </Box>
                        {successPaymentModal.transactionId && (
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="caption" color="textSecondary" fontWeight="bold">N° Transacción:</Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#334155' }}>
                                    {successPaymentModal.transactionId}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ pb: 2.5, px: 3 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => setSuccessPaymentModal(prev => ({ ...prev, open: false }))}
                        sx={{ 
                            bgcolor: '#10b981', 
                            '&:hover': { bgcolor: '#059669' }, 
                            borderRadius: 2.5, 
                            py: 1.3, 
                            fontWeight: 'bold', 
                            textTransform: 'none',
                            fontSize: '0.95rem',
                            boxShadow: '0 4px 14px rgba(16, 185, 129, 0.35)'
                        }}
                    >
                        Entendido / Ver Mis Recibos
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal Selector de Opciones Oficiales de Openpay Perú */}
            <Dialog
                open={openpayOptionsModal.open}
                onClose={() => setOpenpayOptionsModal({ open: false, method: null })}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 4, overflow: 'hidden', maxWidth: 460, boxShadow: '0 20px 40px rgba(0,0,0,0.18)' }
                }}
            >
                <Box sx={{
                    background: 'linear-gradient(135deg, #004481 0%, #072146 100%)',
                    color: 'white',
                    p: 2.5,
                    pb: 3,
                    textAlign: 'center',
                    position: 'relative'
                }}>
                    <IconButton
                        size="small"
                        onClick={() => setOpenpayOptionsModal({ open: false, method: null })}
                        sx={{ position: 'absolute', right: 12, top: 12, color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)', fontWeight: 700, display: 'block', mb: 0.5 }}>
                        Openpay by BBVA
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#38bdf8', fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
                        S/ {paymentDetails.amount ? parseFloat(paymentDetails.amount).toFixed(2) : '0.00'} <Typography component="span" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>PEN</Typography>
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', mt: 0.5 }}>
                        Elige cómo deseas realizar tu pago en Openpay
                    </Typography>
                </Box>

                <DialogContent sx={{ p: 2.5, pt: 3 }}>
                    <Box display="flex" flexDirection="column" gap={1.75}>
                        {/* Opción 1: Tarjeta de crédito/débito y Pasarela Oficial Alojada */}
                        <Paper
                            elevation={0}
                            onClick={handleStartOpenpayRedirect}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                border: '1.5px solid',
                                borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : '#cbd5e1',
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                '&:hover': {
                                    borderColor: '#004481',
                                    bgcolor: 'rgba(0, 68, 129, 0.04)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 20px -4px rgba(0, 68, 129, 0.2)'
                                }
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
                                <Box sx={{
                                    width: 46,
                                    height: 46,
                                    borderRadius: 2.5,
                                    bgcolor: '#004481',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <CardIcon sx={{ color: '#ffffff', fontSize: 24 }} />
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Box display="flex" alignItems="center" gap={1} mb={0.3}>
                                        <Typography variant="body1" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.94rem' }}>
                                            Tarjeta de Crédito / Débito (Pasarela Oficial)
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.76rem', lineHeight: 1.35 }}>
                                        Visa, Mastercard, Amex y Diners en el formulario seguro por defecto de Openpay
                                    </Typography>
                                </Box>
                            </Box>
                            <ChevronRightIcon sx={{ color: '#004481', ml: 1 }} />
                        </Paper>

                        {/* Opción 2: Pago Digital (Banca Móvil / Billeteras) y Agentes con Código OPE */}
                        <Paper
                            elevation={0}
                            onClick={handleGenerateOpenpayStoreCode}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                border: '1.5px solid',
                                borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : '#cbd5e1',
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                '&:hover': {
                                    borderColor: '#0284c7',
                                    bgcolor: 'rgba(2, 132, 199, 0.04)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 20px -4px rgba(2, 132, 199, 0.2)'
                                }
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
                                <Box sx={{
                                    width: 46,
                                    height: 46,
                                    borderRadius: 2.5,
                                    bgcolor: '#0284c7',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <BankIcon sx={{ color: '#ffffff', fontSize: 24 }} />
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Box display="flex" alignItems="center" gap={1} mb={0.3}>
                                        <Typography variant="body1" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.94rem' }}>
                                            Banca Móvil, Billeteras y Agentes (Código de Pago)
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.76rem', lineHeight: 1.35 }}>
                                        Genera un código de pago Openpay (Kashio) para pagar en Apps de BBVA, BCP, Interbank, Scotiabank, Kasnet y Tambo
                                    </Typography>
                                </Box>
                            </Box>
                            <ChevronRightIcon sx={{ color: '#0284c7', ml: 1 }} />
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2, pt: 0, justifyContent: 'space-between' }}>
                    <Button
                        size="small"
                        onClick={() => {
                            setOpenpayOptionsModal({ open: false, method: null });
                            setOpenPaymentSelectionDialog(true);
                        }}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        ← Volver a métodos de pago
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal Oficial de Código de Pago Openpay (Banca Móvil / Agentes - Paynet / Kashio) */}
            <Dialog
                open={openpayStoreModal.open}
                onClose={() => setOpenpayStoreModal(prev => ({ ...prev, open: false }))}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 4, overflow: 'hidden', maxWidth: 480 }
                }}
            >
                <Box sx={{
                    background: 'linear-gradient(135deg, #004481 0%, #072146 100%)',
                    color: 'white',
                    p: 2.5,
                    textAlign: 'center',
                    position: 'relative'
                }}>
                    <IconButton
                        size="small"
                        onClick={() => setOpenpayStoreModal(prev => ({ ...prev, open: false }))}
                        sx={{ position: 'absolute', right: 12, top: 12, color: 'rgba(255,255,255,0.75)', '&:hover': { color: 'white' } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    <Chip
                        label="ORDEN DE PAGO GENERADA"
                        size="small"
                        sx={{ bgcolor: 'rgba(56, 189, 248, 0.2)', color: '#38bdf8', fontWeight: 800, fontSize: '0.7rem', mb: 1 }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: '"Outfit", sans-serif' }}>
                        Código de Pago Openpay (BBVA)
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#38bdf8', mt: 0.5, fontFamily: '"Outfit", sans-serif' }}>
                        S/ {Number(openpayStoreModal.amount || 0).toFixed(2)} PEN
                    </Typography>
                </Box>

                <DialogContent sx={{ p: 2.5 }}>
                    {/* Caja con Código de Referencia OPE... */}
                    <Box sx={{
                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                        border: '2px dashed #0284c7',
                        borderRadius: 3,
                        p: 2,
                        textAlign: 'center',
                        mb: 2.5
                    }}>
                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', display: 'block', mb: 0.5 }}>
                            Código de Referencia de Pago (Kashio / Openpay)
                        </Typography>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1} my={0.5}>
                            <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 900, letterSpacing: '1.5px', color: isDarkMode ? '#38bdf8' : '#004481' }}>
                                {openpayStoreModal.reference || '---'}
                            </Typography>
                            {openpayStoreModal.reference && (
                                <Button
                                    size="small"
                                    variant={openpayStoreModal.copied ? 'contained' : 'outlined'}
                                    color={openpayStoreModal.copied ? 'success' : 'primary'}
                                    startIcon={openpayStoreModal.copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                    onClick={() => {
                                        navigator.clipboard.writeText(openpayStoreModal.reference);
                                        setOpenpayStoreModal(prev => ({ ...prev, copied: true }));
                                        setTimeout(() => setOpenpayStoreModal(prev => ({ ...prev, copied: false })), 2500);
                                    }}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, minWidth: 95 }}
                                >
                                    {openpayStoreModal.copied ? 'Copiado' : 'Copiar'}
                                </Button>
                            )}
                        </Box>

                        {openpayStoreModal.barcodeUrl && (
                            <Box sx={{ mt: 1.5, bgcolor: '#ffffff', p: 1.2, borderRadius: 2, display: 'inline-block', border: '1px solid #e2e8f0' }}>
                                <img
                                    src={openpayStoreModal.barcodeUrl}
                                    alt="Código de barras Openpay"
                                    style={{ maxWidth: '100%', height: 48, display: 'block', margin: '0 auto' }}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Instrucciones de Pago */}
                    <Box sx={{
                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f1f5f9',
                        p: 2,
                        borderRadius: 3,
                        mb: 2
                    }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: 'text.primary' }}>
                            ¿Cómo pagar con tu código?
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary', mb: 1, lineHeight: 1.45 }}>
                            <strong>1. Desde tu App Bancaria o Banca por Internet (BBVA, BCP, Interbank, Scotiabank):</strong> Ingresa a <em>"Pago de Servicios"</em>, busca la empresa <strong>"Kashio"</strong> (u <strong>"Openpay"</strong>), ingresa el código <strong>{openpayStoreModal.reference}</strong> y confirma el pago.
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary', lineHeight: 1.45 }}>
                            <strong>2. En Agentes y Establecimientos (Agentes BBVA, BCP, Interbank, Scotiabank, Kasnet, Tambo):</strong> Indica que realizarás un pago a la empresa <strong>"Kashio"</strong> en Soles con el código <strong>{openpayStoreModal.reference}</strong>.
                        </Typography>
                    </Box>

                    {openpayStoreModal.pdfUrl && (
                        <Button
                            variant="outlined"
                            color="primary"
                            fullWidth
                            startIcon={<DownloadIcon />}
                            component="a"
                            href={openpayStoreModal.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ borderRadius: 2.5, py: 1, mb: 1, textTransform: 'none', fontWeight: 700 }}
                        >
                            Ver / Descargar Recibo Oficial de Pago (PDF Openpay)
                        </Button>
                    )}

                    <Alert severity="info" sx={{ borderRadius: 2.5, py: 0.5, fontSize: '0.78rem' }}>
                        Tu pago se confirmará y activará tu servicio <strong>automáticamente</strong> apenas lo realices.
                    </Alert>
                </DialogContent>

                <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0, flexDirection: 'column', gap: 1 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        disabled={openpayStoreModal.checking || !openpayStoreModal.transactionId}
                        onClick={() => confirmOpenpayTransaction(openpayStoreModal.transactionId, true)}
                        startIcon={openpayStoreModal.checking ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                        sx={{
                            bgcolor: '#004481',
                            '&:hover': { bgcolor: '#072146' },
                            borderRadius: 2.5,
                            py: 1.25,
                            fontWeight: 800,
                            textTransform: 'none'
                        }}
                    >
                        {openpayStoreModal.checking ? 'Verificando estado en Openpay...' : 'Ya realicé mi pago (Verificar Estado)'}
                    </Button>
                    <Button
                        variant="text"
                        fullWidth
                        onClick={() => setOpenpayStoreModal(prev => ({ ...prev, open: false }))}
                        sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
                    >
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ClientPayments;